/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import {
  basicAvailableUsdAtom,
  basicPositionsStateAtom,
  buyBasicRequestAction,
  refreshBasicAction,
  sellBasicRequestAction,
  toRaw,
} from '@root/entities/basic'
import { reatomRequest } from '@root/shared/lib'

import type { BasicPosition } from '@root/entities/basic'
import type { TradeType } from '@root/entities/trade-type'

/**
 * The Basic trading form: an amount in dollars, and a share of a holding.
 *
 * Buying and selling are not symmetric here and the form does not pretend
 * they are. A buy spends dollars, which a person types. A sell disposes of a
 * share of what they hold, because the dollar value of a memecoin position
 * moves between the tap and the fill — asking for "$20 of it" would be a
 * promise nothing can keep.
 */

/** The dollar amounts a buy is usually one of. */
export const BUY_PRESETS_USD = [10, 100, 500, 1000] as const

/** Shares of a holding a sell is usually all of. */
export const SELL_PRESETS_PERCENT = [25, 50, 75, 100] as const

export const basicDirectionAtom = atom<TradeType.SwapDirection>(
  'buy',
  'basicDirectionAtom',
)

export const setBasicDirectionAction = action(
  (ctx, direction: TradeType.SwapDirection) => {
    basicDirectionAtom(ctx, direction)
  },
  'setBasicDirectionAction',
)

/** Exactly what is in the field, as typed. Never a number. */
export const basicAmountRawAtom = atom('', 'basicAmountRawAtom')

export const basicAmountAtom = atom((ctx) => {
  const raw = ctx.spy(basicAmountRawAtom).replaceAll(',', '')
  const amount = BigNumber(raw)

  return amount.isFinite() && amount.isPositive() ? amount : BigNumber(0)
}, 'basicAmountAtom')

export const setBasicAmountRawAction = action((ctx, raw: string) => {
  basicAmountRawAtom(ctx, raw)
}, 'setBasicAmountRawAction')

export const basicSellPercentAtom = atom<number>(100, 'basicSellPercentAtom')

export const setBasicSellPercentAction = action((ctx, percent: number) => {
  basicSellPercentAtom(ctx, percent)
}, 'setBasicSellPercentAction')

/** Everything spendable, put in the field. */
export const setBasicMaximumAmountAction = action((ctx) => {
  const available = pipe(
    ctx.get(basicAvailableUsdAtom),
    AsyncStateN.getOrElseW(() => BigNumber(0)),
  )

  // Down to the cent, and down rather than up: the field has to hold an
  // amount the balance can actually pay.
  basicAmountRawAtom(
    ctx,
    available.decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed(2),
  )
}, 'setBasicMaximumAmountAction')

export type BasicValidation =
  | { kind: 'ok' }
  | { kind: 'no-amount' }
  | { kind: 'not-enough'; available: BigNumber }
  | { kind: 'nothing-held' }

/**
 * Why the button is not ready, if it is not.
 *
 * Said before the request rather than after it: a refusal that comes back
 * from the server is the same refusal a second later, having told the person
 * nothing they could not have been told while typing.
 */
export const basicValidationAtom = atom<BasicValidation>((ctx) => {
  if (ctx.spy(basicDirectionAtom) === 'sell') {
    return ctx.spy(basicPositionAtom)
      ? { kind: 'ok' }
      : { kind: 'nothing-held' }
  }

  const amount = ctx.spy(basicAmountAtom)

  if (amount.isZero()) {
    return { kind: 'no-amount' }
  }

  const available = pipe(
    ctx.spy(basicAvailableUsdAtom),
    AsyncStateN.getOrElseW(() => undefined),
  )

  // While the balance is still loading the amount stands: blocking a buy on
  // a number that has not arrived is worse than letting the server refuse.
  if (available && amount.isGreaterThan(available)) {
    return { kind: 'not-enough', available }
  }

  return { kind: 'ok' }
}, 'basicValidationAtom')

/** The token the panel is pointed at, set by the page it is mounted on. */
export const basicTokenAtom = atom<
  { network: string; address: string; symbol: string } | undefined
>(undefined, 'basicTokenAtom')

export const setBasicTokenAction = action(
  (ctx, token: { network: string; address: string; symbol: string }) => {
    basicTokenAtom(ctx, token)
  },
  'setBasicTokenAction',
)

/** What this account holds of the token on screen, if anything. */
export const basicPositionAtom = atom<BasicPosition | undefined>((ctx) => {
  const token = ctx.spy(basicTokenAtom)

  if (!token) {
    return undefined
  }

  const positions = pipe(
    ctx.spy(basicPositionsStateAtom),
    AsyncStateN.getOrElseW(() => undefined),
  )

  return positions?.positions.find(
    (position) =>
      position.network === token.network &&
      // EVM addresses differ only in case between one source and another.
      position.tokenAddress.toLowerCase() === token.address.toLowerCase(),
  )
}, 'basicPositionAtom')

export const submitBasicTradeModel = reatomRequest({
  name: 'submitBasicTradeModel',
  execAction: action(async (ctx) => {
    const token = ctx.get(basicTokenAtom)

    if (!token) {
      return Result.fail(new Error('no token on screen to trade'))
    }

    const direction = ctx.get(basicDirectionAtom)

    const position = ctx.get(basicPositionAtom)

    if (direction === 'sell' && !position) {
      return Result.fail(new Error('nothing held to sell'))
    }

    const result =
      direction === 'buy'
        ? await ctx.schedule(() =>
            buyBasicRequestAction(ctx, {
              tokenAddress: token.address,
              network: token.network as never,
              amount: toRaw(ctx.get(basicAmountAtom)),
            }),
          )
        : await ctx.schedule(() =>
            sellBasicRequestAction(ctx, {
              positionId: position!.id,
              percent: ctx.get(basicSellPercentAtom),
            }),
          )

    if (Result.isSuccess(result)) {
      // The money is committed the moment the order is accepted, not when
      // it fills. Reading the balance again here is what stops a second
      // order being offered out of dollars the first one has spent.
      await ctx.schedule(() => refreshBasicAction(ctx))
      basicAmountRawAtom(ctx, '')
    }

    return result
  }, 'submitBasicTradeAction'),
})
